import React, { useState, useEffect } from 'react';
import { notification } from 'antd';
import { useHistory } from 'react-router-dom';
import { Window } from '../../utils/window';
import MailIcon from '../../assets/login/MailIcon';
import LoginInput from '../LoginInput/LoginInput';
import { auth0Client } from '../../auth0/auth0';
import loginValidations from '../../hooks/validations/login/loginValidations';
import { t } from '../../i18n/i18n';
import './ForgotPasswordForm.scss';
import { SIGNIN } from '../../constants/routes';
import OTPInputCodePassword from '../OTPInputCodePassword/OTPInputCodePassword';
import { RecoveryPasswordTypes } from '../../constants/enums';
import { getGoogleValidAccount } from '../../API/services/alkymersService';
import { loginWithOTPCode, sendCodeToLogin } from '../../API/services/authService';
import HorizontalLoader from '../HorizontalLoader/HorizontalLoader';
import { handleAuthResult } from '../../auth0/customOTPLogin';
import { useDispatch } from 'react-redux';
import useRoles from '../../hooks/useRoles';
import useSidebar from '../../hooks/useSidebar';

const ForgotPasswordForm = () => {
  const history = useHistory();
  const [loginErrors, setLoginErrors] = useState({});
  const [user, setUser] = useState({
    email: '',
  });
  const { validationsFn } = loginValidations(t);
  const [isDisabled, setIsDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [sendCode, setSendCode] = useState(false);
  const [sendMail, setSendMail] = useState(false);
  const initialCodeOTP = Array.from({ length: 4 }, () => '')
  const [codeOTP, setCodeOTP] = useState(initialCodeOTP);
  const [loadingCode, setLoadingCode] = useState(false);

  const dispatch = useDispatch();
  const { roles } = useRoles();
  const { setCollapsed } = useSidebar();

  useEffect(() => {
    setLoginErrors({});
    setUser({ email: '' });
  }, []);

  const onChange = (e) => {
    if (
      e.target.value.includes('@') &&
      e.target.value.split('.')?.length > 1
    ) {
      delete loginErrors[e.target.name];
      setUser({ ...user, [e.target.name]: e.target.value });
    } else {
      const errors = validationsFn(e.target.name, e.target.value, true);
      if (errors?.length > 0) {
        setLoginErrors({
          ...loginErrors,
          [e.target.name]: errors,
        });
      } else {
        delete loginErrors[e.target.name];
      }
      setUser({ ...user, [e.target.name]: e.target.value });
    }
  };

  const startCountdown = () => {
    setIsDisabled(true);
    setCountdown(60);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleRecover = async (type) => {
    if (Window()) {
      switch (type) {
        case RecoveryPasswordTypes.MAIL:
          setSendMail(true)
          startCountdown();
          try {
            const res = await getGoogleValidAccount(user.email);
            if (!res.data) {
              notification.open({
                message: t('ERROR_GOOGLE_ACCOUNT'),
                type: 'error',
              });
            } else {
              auth0Client().changePassword(
                {
                  connection: 'Username-Password-Authentication',
                  email: user.email,
                },
                (err) => {
                  if (err) {
                    notification.open({
                      message: t('RECOVER_PASSWORD_ERROR'),
                      type: 'error',
                    });
                  } else {
                    notification.open({
                      message: t('RECOVER_PASSWORD_SUCCESS'),
                      type: 'success',
                    });
                  }
                }
              );
            };
          } catch {
            notification.open({
              message: t('RECOVER_PASSWORD_ERROR'),
              type: 'error',
            });
          };
          break;
        case RecoveryPasswordTypes.CODE:
          const bodyToCode = {
            email: user.email,
            platform: 'PARTNERS'
          }
          await sendEmailLoginCode(bodyToCode);
          break;

        default:
          break;
      };
    };
  };

  const sendEmailLoginCode = async (body) => {
    setLoadingCode(true);
    if (Window()) {
      try {
        await sendCodeToLogin(body);
        notification.open({
          message: t('RECOVER_PASSWORD_SUCCESS'),
          type: 'success',
        });
        setSendCode(true);
      } catch {
        notification.open({
          message: t('ERROR_GOOGLE_ACCOUNT'),
          type: 'error',
        });
      } finally {
        setLoadingCode(false);
      };
    };
  };

  const loginWithCode = async () => {
    const body = {
      email: user.email,
      platform: 'PARTNERS',
      otp: codeOTP.join('')
    };

    setLoadingCode(true);

    try {
      const response = await loginWithOTPCode(body);
      await handleAuthResult({ result: response, dispatch, roles, setCollapsed });
    } catch {
      notification.open({
        message: t('OTP_PASSWORD_ERROR'),
        type: 'error',
      });
      setLoadingCode(false);
    };
  };

  const resetForm = () => {
    setUser({
      email: '',
    });
  };

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className="d-flex login-form-container py-2 mx-auto flex-column justify-content-start align-items-center gap-2"
    >
      {
        !sendCode &&
        <LoginInput
          placeholder={t('EMAIL_PLACEHOLDER')}
          name="email"
          value={user?.email}
          onChange={onChange}
          error={loginErrors?.email && loginErrors?.email[0]}
          Icon={MailIcon}
        />
      }
      {
        sendCode ?
          <>
            <OTPInputCodePassword codeOTP={codeOTP} setCodeOTP={setCodeOTP} />
            <button
              data-testid="RECOVER_PASSWORD_SEND_EMAIL"
              onClick={loginWithCode}
              type="submit"
              disabled={codeOTP.some(codeChar => codeChar === '')}
              className={`rounded-sm border-0 text-white text-xs font-weight-bold login-form-button w-50 bg-maya-blue ${codeOTP.some(codeChar => codeChar === '') ? 'disabled' : ''}`}
            >
              {t('LOGIN_WITH_CODE_OTP_BUTTON')}
            </button>
            {loadingCode && (
              <div className="d-flex justify-content-center">
                <HorizontalLoader className="h-auto my-4" />
              </div>
            )}
            <button
              data-testid="RECOVER_PASSWORD_CANCEL"
              onClick={() => {
                setSendCode(false);
                setCodeOTP(initialCodeOTP);
              }}
              type="button"
              className="bg-transparent font-weight-500 w-50 text-xs mt-3 mt-md-0 text-blue-principal border-0"
            >
              {t('CANCEL')}
            </button>
          </>
          : sendMail ?
            <div className="d-flex flex-column gap-2 w-50">
              {!isDisabled ? (
                <button
                  data-testid="RECOVER_PASSWORD_SEND_EMAIL"
                  onClick={() => handleRecover(RecoveryPasswordTypes.MAIL)}
                  type="submit"
                  disabled={!user?.email || !!loginErrors?.email}
                  className={`rounded-sm border-0 text-white text-xs font-weight-bold login-form-button ${!user?.email || !!loginErrors?.email ? 'bg-maya-blue disabled' : 'bg-maya-blue'
                    }`}
                >
                  {t('RECOVER_PASSWORD_SEND_EMAIL')}
                </button>
              ) : (
                <p className="mt-1 font-weight-600 text-xs text-grey font-semibold dark:text-white">
                  {`${t('WAIT_TO_RESEND_EMAIL_VERIFY')} ${countdown}s`}
                </p>
              )}
              <button
                data-testid="RECOVER_PASSWORD_CANCEL"
                onClick={() => {
                  setSendMail(false);
                }}
                type="button"
                className="bg-transparent font-weight-500  text-xs mt-3 mt-md-0 text-blue-principal border-0"
              >
                {t('CANCEL')}
              </button>
            </div>
            :
            <div className="d-flex flex-column gap-4 w-100">
              <div className="d-flex justify-content-around">
                <button
                  data-testid="RECOVER_PASSWORD_SEND_EMAIL"
                  onClick={() => handleRecover(RecoveryPasswordTypes.CODE)}
                  type="submit"
                  disabled={!user?.email || !!loginErrors?.email}
                  className={`rounded-sm border-0 text-white text-xs font-weight-bold login-form-button bg-maya-blue ${!user?.email || !!loginErrors?.email ? 'disabled' : ''
                    }`}
                >
                  {t('LOGIN_WITH_CODE_OTP_MESSAGE')}
                </button>
                <button
                  data-testid="RECOVER_PASSWORD_SEND_EMAIL"
                  onClick={() => handleRecover(RecoveryPasswordTypes.MAIL)}
                  type="submit"
                  disabled={!user?.email || !!loginErrors?.email}
                  className={`rounded-sm border-0 text-white text-xs font-weight-bold login-form-button bg-maya-blue ${!user?.email || !!loginErrors?.email ? 'disabled' : ''
                    }`}
                >
                  {t('RESET_PASSWORD_TO_LOGIN')}
                </button>
              </div>
              {loadingCode && (
                <div className="d-flex justify-content-center">
                  <HorizontalLoader className="h-auto my-4" />
                </div>
              )}
              <button
                data-testid="RECOVER_PASSWORD_CANCEL"
                onClick={() => {
                  resetForm();
                  history.push(SIGNIN);
                }}
                type="button"
                className="bg-transparent font-weight-500  text-xs mt-3 mt-md-0 text-blue-principal border-0"
              >
                {t('RETURN_TO_LOGIN')}
              </button>
            </div>
      }

    </form>
  );
};

export default ForgotPasswordForm;
